import React, { useCallback, useEffect, useState } from "react";
import { DatePicker, Select, Table, Tag, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  getAttendance,
  getAttendancePunches,
  getBiometricDevices,
  createBiometricDevice,
  updateBiometricDevice,
  getDeviceMappings,
  createDeviceMapping,
  deleteDeviceMapping,
} from "../../apis/attendance";
import { getClinicUsers } from "../../apis/user";
import Modal from "../../components/Modal";
import AttendanceOverrideModal from "./AttendanceOverrideModal";
import { useNotification } from "../../NotificationContext";

const { RangePicker } = DatePicker;

const STATUS_COLORS: Record<string, string> = {
  present:  "green",
  late:     "orange",
  absent:   "red",
  half_day: "blue",
  leave:    "purple",
  holiday:  "cyan",
};

const formatTime = (iso: string | null | undefined) => {
  if (!iso) return "—";
  return dayjs(iso).format("HH:mm");
};

// ─── Punch Log Modal ────────────────────────────────────────────────────────

const PunchLogModal: React.FC<{
  clinicUserId: number;
  userName: string;
  date: string;
  onClose: () => void;
}> = ({ clinicUserId, userName, date, onClose }) => {
  const [punches, setPunches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getAttendancePunches({ clinicUserId, dateFrom: date, dateTo: date, perPage: 100 })
      .then((res: any) => setPunches(res.data?.data || []))
      .finally(() => setLoading(false));
  }, [clinicUserId, date]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">
        Raw punches for <strong>{userName}</strong> on {date}
      </p>
      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : punches.length === 0 ? (
        <p className="text-sm text-gray-400">No punches recorded for this date.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-400 border-b">
              <th className="pb-1">Time</th>
              <th className="pb-1">Type</th>
              <th className="pb-1">Device</th>
              <th className="pb-1">PIN</th>
            </tr>
          </thead>
          <tbody>
            {punches.map((p: any) => (
              <tr key={p.id} className="border-b last:border-0">
                <td className="py-1.5 font-mono">{dayjs(p.punchedAt).format("HH:mm:ss")}</td>
                <td className="py-1.5">{p.punchType || "—"}</td>
                <td className="py-1.5 text-gray-500 text-xs">{p.deviceId || "—"}</td>
                <td className="py-1.5 text-gray-500 text-xs">{p.deviceEmployeeCode}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="pt-2 border-t border-gray-100">
        <button onClick={onClose} className="text-sm font-semibold text-gray-600 hover:text-gray-900">
          Close
        </button>
      </div>
    </div>
  );
};

// ─── Device Management Tab ──────────────────────────────────────────────────

const DevicesTab: React.FC = () => {
  const { showNotification } = useNotification();
  const [devices, setDevices] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [editDevice, setEditDevice] = useState<any | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<any | null>(null);
  const [mappings, setMappings] = useState<any[]>([]);
  const [clinicUsers, setClinicUsers] = useState<any[]>([]);
  const [newPin, setNewPin] = useState("");
  const [newUserId, setNewUserId] = useState<number | null>(null);
  const [deviceForm, setDeviceForm] = useState({ serialNumber: "", deviceName: "", location: "" });

  const fetchDevices = () => {
    setLoading(true);
    getBiometricDevices()
      .then((res: any) => setDevices(res.data?.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDevices();
    getClinicUsers().then((res: any) => setClinicUsers(res.data?.data || res.data || []));
  }, []);

  const handleSelectDevice = (device: any) => {
    setSelectedDevice(device);
    getDeviceMappings(device.id).then((res: any) => setMappings(res.data?.data || []));
  };

  const handleSaveDevice = async () => {
    try {
      if (editDevice) {
        await updateBiometricDevice(editDevice.id, {
          deviceName: deviceForm.deviceName,
          location:   deviceForm.location,
        });
        showNotification("success", "Device updated", "", "topRight");
      } else {
        await createBiometricDevice(deviceForm);
        showNotification("success", "Device registered", "", "topRight");
      }
      setShowAddDevice(false);
      setEditDevice(null);
      setDeviceForm({ serialNumber: "", deviceName: "", location: "" });
      fetchDevices();
    } catch (err: any) {
      showNotification("error", err?.response?.data?.message || "Failed", "", "topRight");
    }
  };

  const handleAddMapping = async () => {
    if (!selectedDevice || !newPin || !newUserId) return;
    try {
      await createDeviceMapping(selectedDevice.id, { devicePin: newPin, clinicUserId: newUserId });
      showNotification("success", "Mapping added", "", "topRight");
      setNewPin("");
      setNewUserId(null);
      getDeviceMappings(selectedDevice.id).then((res: any) => setMappings(res.data?.data || []));
    } catch (err: any) {
      showNotification("error", err?.response?.data?.message || "Failed", "", "topRight");
    }
  };

  const handleDeleteMapping = async (id: number) => {
    try {
      await deleteDeviceMapping(id);
      setMappings((prev) => prev.filter((m) => m.id !== id));
    } catch {
      showNotification("error", "Failed to delete", "", "topRight");
    }
  };

  return (
    <div className="grid grid-cols-2 gap-6">
      {/* Device list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Registered Devices</h3>
          <button
            onClick={() => { setEditDevice(null); setDeviceForm({ serialNumber: "", deviceName: "", location: "" }); setShowAddDevice(true); }}
            className="text-xs px-3 py-1.5 border border-indigo-300 text-indigo-600 rounded-md hover:bg-indigo-50"
          >
            + Add Device
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : devices.length === 0 ? (
          <p className="text-sm text-gray-400">No devices registered yet.</p>
        ) : (
          <div className="space-y-2">
            {devices.map((d: any) => (
              <div
                key={d.id}
                onClick={() => handleSelectDevice(d)}
                className={`rounded-lg border p-3 cursor-pointer hover:border-indigo-300 transition-colors ${
                  selectedDevice?.id === d.id ? "border-indigo-400 bg-indigo-50" : "border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{d.deviceName || d.serialNumber}</p>
                    <p className="text-xs text-gray-400">{d.serialNumber} {d.location ? `· ${d.location}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${d.status === "active" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {d.status}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditDevice(d); setDeviceForm({ serialNumber: d.serialNumber, deviceName: d.deviceName || "", location: d.location || "" }); setShowAddDevice(true); }}
                      className="text-xs text-gray-400 hover:text-indigo-600"
                    >
                      Edit
                    </button>
                  </div>
                </div>
                {d.lastSeenAt && (
                  <p className="text-[10px] text-gray-400 mt-1">Last seen: {dayjs(d.lastSeenAt).format("DD MMM HH:mm")}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add/edit device form inline */}
        {showAddDevice && (
          <div className="mt-4 rounded-lg border border-gray-200 p-4 space-y-3 bg-gray-50">
            <p className="text-sm font-semibold">{editDevice ? "Edit Device" : "Register Device"}</p>
            {!editDevice && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Serial Number *</label>
                <input
                  type="text"
                  value={deviceForm.serialNumber}
                  onChange={(e) => setDeviceForm((p) => ({ ...p, serialNumber: e.target.value }))}
                  placeholder="e.g. AC123456789"
                  className="w-full rounded border py-1 px-2 text-sm border-gray-300 focus:outline-none focus:border-indigo-400"
                />
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Device Name</label>
              <input
                type="text"
                value={deviceForm.deviceName}
                onChange={(e) => setDeviceForm((p) => ({ ...p, deviceName: e.target.value }))}
                placeholder="e.g. Main Entrance"
                className="w-full rounded border py-1 px-2 text-sm border-gray-300 focus:outline-none focus:border-indigo-400"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Location</label>
              <input
                type="text"
                value={deviceForm.location}
                onChange={(e) => setDeviceForm((p) => ({ ...p, location: e.target.value }))}
                placeholder="e.g. Ground Floor"
                className="w-full rounded border py-1 px-2 text-sm border-gray-300 focus:outline-none focus:border-indigo-400"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={handleSaveDevice} className="px-3 py-1.5 text-xs font-semibold text-white bg-primary rounded hover:bg-hover">
                Save
              </button>
              <button onClick={() => setShowAddDevice(false)} className="text-xs text-gray-500 hover:text-gray-800">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mappings panel */}
      <div>
        {!selectedDevice ? (
          <div className="flex items-center justify-center h-40 rounded-lg border border-dashed border-gray-200">
            <p className="text-sm text-gray-400">Select a device to manage PIN mappings</p>
          </div>
        ) : (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-1">
              PIN → Employee mappings for <span className="text-indigo-600">{selectedDevice.deviceName || selectedDevice.serialNumber}</span>
            </h3>
            <p className="text-xs text-gray-400 mb-3">Webhook token: <code className="bg-gray-100 px-1 rounded">{selectedDevice.webhookToken}</code></p>

            {/* Add mapping row */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                placeholder="Device PIN"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                className="w-28 rounded border py-1 px-2 text-sm border-gray-300 focus:outline-none focus:border-indigo-400"
              />
              <Select
                showSearch
                placeholder="Select employee"
                className="flex-1"
                value={newUserId ?? undefined}
                onChange={setNewUserId}
                filterOption={(input, option) =>
                  String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={clinicUsers.map((u: any) => ({
                  value: u.id,
                  label: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || `User ${u.id}`,
                }))}
                size="small"
              />
              <button
                onClick={handleAddMapping}
                className="px-3 py-1 text-xs font-semibold text-white bg-primary rounded hover:bg-hover whitespace-nowrap"
              >
                Add
              </button>
            </div>

            {mappings.length === 0 ? (
              <p className="text-sm text-gray-400">No mappings yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b">
                    <th className="pb-1">PIN</th>
                    <th className="pb-1">Employee</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m: any) => (
                    <tr key={m.id} className="border-b last:border-0">
                      <td className="py-1.5 font-mono text-xs">{m.devicePin}</td>
                      <td className="py-1.5">{m.userName || `User ${m.clinicUserId}`}</td>
                      <td className="py-1.5 text-right">
                        <button
                          onClick={() => handleDeleteMapping(m.id)}
                          className="text-xs text-red-400 hover:text-red-600"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Attendance View ────────────────────────────────────────────────────

const AttendanceView: React.FC = () => {
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(30);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("week"),
    dayjs().endOf("week"),
  ]);
  const [clinicUserId, setClinicUserId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [clinicUsers, setClinicUsers] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<"records" | "devices">("records");

  const [overrideModal, setOverrideModal] = useState<{ open: boolean; record: any | null }>({ open: false, record: null });
  const [punchModal, setPunchModal] = useState<{ open: boolean; record: any | null }>({ open: false, record: null });

  const fetchRecords = useCallback(() => {
    setLoading(true);
    getAttendance({
      dateFrom:     dateRange[0].format("YYYY-MM-DD"),
      dateTo:       dateRange[1].format("YYYY-MM-DD"),
      clinicUserId: clinicUserId ?? undefined,
      status:       statusFilter ?? undefined,
      page,
      perPage,
    })
      .then((res: any) => {
        setRecords(res.data?.data || []);
        setTotal(res.data?.meta?.total || 0);
      })
      .finally(() => setLoading(false));
  }, [dateRange, clinicUserId, statusFilter, page, perPage]);

  useEffect(() => {
    getClinicUsers().then((res: any) => setClinicUsers(res.data?.data || res.data || []));
  }, []);

  useEffect(() => {
    if (activeTab === "records") fetchRecords();
  }, [fetchRecords, activeTab]);

  const columns = [
    {
      title: "Employee",
      dataIndex: "userName",
      key: "userName",
      render: (name: string, row: any) => (
        <div>
          <p className="font-medium text-sm">{name || `User ${row.clinicUserId}`}</p>
          {row.userDegree && <p className="text-xs text-gray-400">{row.userDegree}</p>}
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "attendanceDate",
      key: "attendanceDate",
      render: (d: string) => <span className="text-sm">{dayjs(d).format("DD MMM YYYY")}</span>,
    },
    {
      title: "Shift",
      key: "shift",
      render: (_: any, row: any) =>
        row.shiftName ? (
          <span className="text-xs text-gray-500">
            {row.shiftName} ({row.shiftStart?.slice(0, 5)} – {row.shiftEnd?.slice(0, 5)})
          </span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        ),
    },
    {
      title: "Check In",
      dataIndex: "checkInTime",
      key: "checkInTime",
      render: (t: string, row: any) => (
        <div>
          <span className="font-mono text-sm">{formatTime(t)}</span>
          {row.isLate && row.lateByMinutes && (
            <span className="ml-1.5 text-[10px] text-orange-500">+{row.lateByMinutes}m late</span>
          )}
        </div>
      ),
    },
    {
      title: "Check Out",
      dataIndex: "checkOutTime",
      key: "checkOutTime",
      render: (t: string, row: any) => (
        <div>
          <span className="font-mono text-sm">{formatTime(t)}</span>
          {row.earlyDeparture && <span className="ml-1.5 text-[10px] text-orange-500">early out</span>}
          {row.checkoutMissing && !row.earlyDeparture && (
            <span className="ml-1.5 text-[10px] text-red-400">missing</span>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: string, row: any) => (
        <div className="flex items-center gap-1">
          <Tag color={STATUS_COLORS[s] || "default"} className="capitalize text-xs">
            {s.replace("_", " ")}
          </Tag>
          {row.isManualOverride && (
            <Tooltip title={`Override by ${row.overrideBy || "admin"}${row.overrideReason ? `: ${row.overrideReason}` : ""}`}>
              <span className="text-[10px] text-indigo-400 cursor-help">✎</span>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, row: any) => (
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPunchModal({ open: true, record: row })}
            className="text-xs text-gray-400 hover:text-gray-700"
          >
            Punches
          </button>
          <button
            onClick={() => setOverrideModal({ open: true, record: row })}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Override
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Attendance</h1>
          <p className="text-sm text-gray-400 mt-0.5">View and manage staff attendance records</p>
        </div>
        <button
          onClick={() => setOverrideModal({ open: true, record: null })}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary rounded-md hover:bg-hover"
        >
          + Manual Entry
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-gray-200">
        {(["records", "devices"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
              activeTab === tab
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab === "records" ? "Attendance Records" : "Biometric Devices"}
          </button>
        ))}
      </div>

      {activeTab === "records" && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <RangePicker
              value={dateRange}
              onChange={(val) => val && setDateRange(val as [Dayjs, Dayjs])}
              format="DD MMM YYYY"
            />
            <Select
              allowClear
              showSearch
              placeholder="All employees"
              className="w-52"
              value={clinicUserId ?? undefined}
              onChange={(v) => { setClinicUserId(v ?? null); setPage(1); }}
              filterOption={(input, option) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={clinicUsers.map((u: any) => ({
                value: u.id,
                label: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || `User ${u.id}`,
              }))}
            />
            <Select
              allowClear
              placeholder="All statuses"
              className="w-36"
              value={statusFilter ?? undefined}
              onChange={(v) => { setStatusFilter(v ?? null); setPage(1); }}
              options={[
                { value: "present",  label: "Present" },
                { value: "late",     label: "Late" },
                { value: "absent",   label: "Absent" },
                { value: "half_day", label: "Half Day" },
                { value: "leave",    label: "Leave" },
                { value: "holiday",  label: "Holiday" },
              ]}
            />
          </div>

          <Table
            dataSource={records}
            columns={columns}
            rowKey="id"
            loading={loading}
            size="small"
            pagination={{
              current: page,
              pageSize: perPage,
              total,
              onChange: setPage,
              showSizeChanger: false,
            }}
            locale={{ emptyText: "No attendance records found for this period." }}
          />
        </>
      )}

      {activeTab === "devices" && <DevicesTab />}

      {/* Override / manual entry modal */}
      {overrideModal.open && (
        <Modal
          title={overrideModal.record ? "Override Attendance" : "Manual Attendance Entry"}
          onClose={() => setOverrideModal({ open: false, record: null })}
          modalContent={
            <AttendanceOverrideModal
              record={overrideModal.record}
              onClose={() => setOverrideModal({ open: false, record: null })}
              onSaved={() => { setOverrideModal({ open: false, record: null }); fetchRecords(); }}
            />
          }
        />
      )}

      {/* Punch log modal */}
      {punchModal.open && punchModal.record && (
        <Modal
          title="Punch Log"
          onClose={() => setPunchModal({ open: false, record: null })}
          modalContent={
            <PunchLogModal
              clinicUserId={punchModal.record.clinicUserId}
              userName={punchModal.record.userName || ""}
              date={punchModal.record.attendanceDate}
              onClose={() => setPunchModal({ open: false, record: null })}
            />
          }
        />
      )}
    </div>
  );
};

export default AttendanceView;
